import React, { useState } from "react";
import PropTypes from "prop-types";
import {
  Modal, ModalHeader, ModalBody, ModalFooter, Button, Spinner,
} from "reactstrap";
import { Alert } from "../ui";

/**
 * 共用確認 / 刪除阻擋對話框（主計劃：刪除前檢查、送出確認）。
 *
 * 一般確認：
 *   <ConfirmDialog isOpen toggle title="確認刪除" message="確定刪除此筆？"
 *                  confirmColor="danger" onConfirm={handleDelete} />
 *
 * 刪除阻擋（後端回 409 + 原因）時，帶入 blockedReason 只顯示原因與關閉：
 *   <ConfirmDialog isOpen toggle title="無法刪除" blockedReason="仍有機櫃綁定…" />
 */
export const ConfirmDialog = ({
  isOpen, toggle, title = "請確認", message,
  confirmText = "確認", cancelText = "取消",
  confirmColor = "primary", onConfirm, blockedReason, blockedAction,
}) => {
  // 刪除類確認(confirmColor="danger"):稿上標題為紅字 + 警告 icon
  // (2128:24469 機房刪除確認 / 2220:77307 區域版;drift check 8/12)
  const dangerTitle = confirmColor === "danger";
  const [busy, setBusy] = useState(false);

  const handleConfirm = async () => {
    if (!onConfirm) return;
    setBusy(true);
    try {
      await onConfirm();
      toggle(); // 成功才關閉
    } catch (e) {
      // onConfirm 失敗(如刪除阻擋設定了 blockedReason)→ 保持開啟顯示原因,不關閉
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal isOpen={isOpen} toggle={toggle} centered scrollable>
      <ModalHeader toggle={toggle}>
        {dangerTitle ? (
          <span className="text-danger">
            <i className="bi bi-exclamation-triangle-fill me-2" />{title}
          </span>
        ) : title}
      </ModalHeader>
      <ModalBody>
        {blockedReason ? (
          // 阻擋原因可能有多行(第二行講怎麼解除),換行要留著
          <Alert color="warning" className="mb-0" style={{ whiteSpace: "pre-line" }}>
            <i className="mdi mdi-alert-outline me-1" />{blockedReason}
          </Alert>
        ) : (
          // 稿上的刪除說明是分段的(硬體 2128:28325／軟體 2128:28346 中間有一個空段),
          // 換行要留著 —— 全部擠成一段會讓「歷史紀錄仍保留」黏在警告後面
          <p className="mb-0" style={{ whiteSpace: "pre-line" }}>{message}</p>
        )}
      </ModalBody>
      <ModalFooter>
        {blockedReason ? (
          <>
            {/* 只說「不能刪」等於把人丟在原地 —— 給一條走得出去的路 */}
            {blockedAction}
            <Button color="light" onClick={toggle}>我知道了</Button>
          </>
        ) : (
          <>
            {/* 稿上取消為 Secondary 實底 #6C757D,不是淺底 light */}
            <Button color="secondary" onClick={toggle}>{cancelText}</Button>
            <Button color={confirmColor} disabled={busy} onClick={handleConfirm}>
              {busy ? <Spinner size="sm" className="me-1" /> : null}{confirmText}
            </Button>
          </>
        )}
      </ModalFooter>
    </Modal>
  );
};
ConfirmDialog.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  toggle: PropTypes.func.isRequired,
  title: PropTypes.string,
  message: PropTypes.node,
  confirmText: PropTypes.string,
  cancelText: PropTypes.string,
  confirmColor: PropTypes.string,
  onConfirm: PropTypes.func,
  blockedReason: PropTypes.string,
  // 阻擋時額外給的行動入口(例如「前往工作管理」)
  blockedAction: PropTypes.node,
};

/**
 * 刪除流程 hook：呼叫 remove()，若後端回 409（刪除阻擋）擷取原因交給對話框顯示。
 *
 *   const del = useDeleteFlow((id)=> roomsApi.remove(id), refetch);
 *   <Button onClick={()=>del.ask(row)}>刪除</Button>
 *   <ConfirmDialog {...del.dialogProps} title="刪除機房" message="確定刪除？" confirmColor="danger" />
 */
export function useDeleteFlow(removeFn, onDone) {
  const [target, setTarget] = useState(null);
  const [blockedReason, setBlockedReason] = useState(null);

  const ask = (row) => { setBlockedReason(null); setTarget(row); };
  const close = () => { setTarget(null); setBlockedReason(null); };

  const confirm = async () => {
    try {
      await removeFn(target.id ?? target);
      close();
      if (onDone) onDone();
    } catch (err) {
      // 刪除阻擋(如 409):設定原因並 re-throw,讓 ConfirmDialog 保持開啟顯示原因
      setBlockedReason(typeof err === "string" ? err : "此資料仍被引用，無法刪除。");
      throw err;
    }
  };

  return {
    ask,
    // 對話框文案要講得出「刪的是誰」(如 2128:28288 帶機櫃編號),把目標一併給出去
    target,
    dialogProps: {
      isOpen: !!target,
      toggle: close,
      onConfirm: confirm,
      blockedReason,
    },
  };
}

/**
 * MSG-03-01 刪除確認彈窗的文案 builder。
 *
 * 稿上 IP(`2375:31167`)與合約(`2375:32802`)的刪除確認彈窗**掛的是同一個編號 MSG-03-01** ——
 * 設計端就當它是一個元件,所以文案結構收在這裡,不讓每頁各寫一份(專案/廠商頁跟上時直接用)。
 * 三句固定順序:**刪的是哪一筆** → 核准後會怎樣(依 `approvalStepEnabled()` 分兩條) → 稽核留多久。
 *
 *   deleteDialogText({
 *     title: "確認刪除 IP？",
 *     subject: target?.ip && `IP 資訊 ${target.ip}`,   // 落空時退到 fallback 句
 *     fallbackSubject: "這筆 IP 資訊",
 *     noun: "該筆 IP",
 *     retains: "歷史操作與審核紀錄",
 *   })
 *
 * `approval` 由呼叫端傳 `approvalStepEnabled()` 進來,不在這裡 import ——
 * `ApprovalFlow` 吃了 dck_helper／useResource／RbacGuard 一大串,而 `ConfirmDialog`
 * 是最底層的小元件,反向依賴會繞出循環。
 *
 * @param {object}  o
 * @param {string}  o.title            彈窗標題
 * @param {string}  [o.subject]        有識別時的說法(如 `IP 資訊 10.20.1.8`);Latin 開頭故前面補空格
 * @param {string}  o.fallbackSubject  沒有識別時的說法(如 `這筆 IP 資訊`),中文開頭不補空格
 * @param {string}  o.noun             「核准後{noun}將自系統移除」裡的主詞
 * @param {string}  [o.cascade]        連帶影響(如合約的關聯解除);不給就不出現這一段
 * @param {string}  o.retains          「{retains}仍保留供稽核查詢（留存 180 天）。」
 * @param {boolean} o.approval         審核關卡是否開啟(`approvalStepEnabled()`)
 */
/**
 * 中英混排補空格:Latin 與中文相鄰時要留一個空格(「核准後該筆 IP 將自系統移除」的那個)。
 * 只補在真的相鄰的那一邊 —— 後面接的是全形句號時不補,不然會變成「10.20.1.8 。」。
 */
const padBefore = (s = "") => s.replace(/^([A-Za-z0-9])/, " $1");
const padAfter = (s = "") => s.replace(/([A-Za-z0-9])$/, "$1 ");

export function deleteDialogText({
  title, subject, fallbackSubject, noun, cascade = "", retains, approval,
}) {
  // subject 後面接全形句號 → 只補前緣;noun 後面接「將」 → 只補後緣
  const who = subject ? padBefore(subject) : fallbackSubject;
  const what = padAfter(noun);
  return {
    title,
    message: [
      `您即將刪除${who}。`,
      approval
        ? `此操作將送入審核，核准後${what}將自系統移除${cascade}。`
        : `${what}將自系統移除，且無法復原${cascade}。`,
      `${retains}仍保留供稽核查詢（留存 180 天）。`,
    ].join("\n"),
    confirmColor: "danger",
    confirmText: approval ? "送出刪除審核" : "確認，刪除",
  };
}

export default ConfirmDialog;
